import React, { useState, useEffect } from 'react'
import '@styles/client-admin/compact-official-form.scss'
import api, { getStorageUrl } from '../../services/apiService'

const CompactOfficialForm = ({ official, onClose, onSuccess, showToast }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_name: '',
    official_type: 'state_official',
    position: '',
    aadhar_card: '',
    passport_number: '',
    contact: '',
    emergency_contact: '',
    email: '',
    marital_status: 'single',
    marriage_date: '',
    category: '' // Will be auto-calculated from DOB
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [masterCategories, setMasterCategories] = useState([])

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  // Get category based on age from master categories
  const getAgeCategory = (age) => {
    if (!age) return ''
    // Use master categories if available
    if (masterCategories.length > 0) {
      const matchedCategory = masterCategories.find(cat =>
        age >= cat.min_age && (cat.max_age === null || age <= cat.max_age)
      )
      if (matchedCategory) return matchedCategory.name
    }
    // Fallback to hardcoded values
    if (age <= 12) return 'Little League'
    if (age <= 15) return 'Sub Junior'
    if (age <= 18) return 'Junior'
    return 'Senior'
  }

  const getAgeCategoryDisplay = (age) => {
    if (!age) return ''
    // Use master categories if available
    if (masterCategories.length > 0) {
      const matchedCategory = masterCategories.find(cat =>
        age >= cat.min_age && (cat.max_age === null || age <= cat.max_age)
      )
      if (matchedCategory) return matchedCategory.name
    }
    // Fallback to hardcoded values
    if (age <= 12) return 'U-12 (Little League)'
    if (age <= 15) return 'U-15 (Sub Junior)'
    if (age <= 18) return 'U-18 (Junior)'
    return 'Senior'
  }

  const currentAge = calculateAge(formData.date_of_birth)

  // Fetch master categories on mount
  useEffect(() => {
    fetchMasterCategories()
  }, [])

  const fetchMasterCategories = async () => {
    try {
      const response = await api.get('/client-admin/categories')
      if (response.data.success || response.data.data) {
        setMasterCategories(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  useEffect(() => {
    if (official) {
      // Calculate category from DOB for existing official
      const age = calculateAge(official.dob)
      const category = official.category || getAgeCategory(age)

      setFormData({
        full_name: official.fullName || '',
        father_name: official.fatherName || '',
        date_of_birth: official.dob || '',
        gender: official.gender === 'Male' ? 'M' : 'F',
        blood_group: official.bloodGroup || '',
        height: official.height || '',
        weight: official.weight || '',
        address: official.address || '',
        pin_code: official.pinCode || '',
        district: official.district || '',
        state_name: official.state || '',
        official_type: official.officialType || 'state_official',
        position: official.position || '',
        aadhar_card: official.aadharCard || '',
        passport_number: official.passportNum || '',
        contact: official.contact || '',
        emergency_contact: official.emergencyContact || '',
        email: official.email || '',
        marital_status: official.maritalStatus?.toLowerCase() || 'single',
        marriage_date: official.marriageDate || '',
        category: category
      })
      // Set image preview if official has image
      if (official.image) {
        setImagePreview(getStorageUrl(official.image))
      }
    }
  }, [official, masterCategories])

  const handleChange = (e) => {
    const { name, value } = e.target

    // Auto-calculate category when DOB changes
    if (name === 'date_of_birth' && value) {
      const age = calculateAge(value)
      const category = getAgeCategory(age)
      console.log('Category auto-calculated:', category, 'for age:', age)
      setFormData(prev => ({
        ...prev,
        [name]: value,
        category: category
      }))
      // Clear error for this field
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }))
      }
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showToast?.error('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast?.error('Image size should be less than 5MB')
        return
      }

      setImageFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required'
    }

    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required'
    }

    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact number is required'
    } else if (!/^\d{10}$/.test(formData.contact)) {
      newErrors.contact = 'Contact must be 10 digits'
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Use category from formData (already auto-calculated when DOB was entered)
      // If not set, calculate now as fallback
      const category = formData.category || getAgeCategory(calculateAge(formData.date_of_birth))

      const dataToSubmit = {
        ...formData,
        category: category
      }

      let response

      // If there's an image file, use FormData
      if (imageFile) {
        const formDataToSend = new FormData()

        // Append all form fields including auto-calculated category
        Object.keys(dataToSubmit).forEach(key => {
          formDataToSend.append(key, dataToSubmit[key])
        })

        // Append image file
        formDataToSend.append('official_image', imageFile)

        // For updates, add _method field to indicate PUT and append ID
        if (official) {
          formDataToSend.append('_method', 'PUT')
          formDataToSend.append('id', official.id)
          response = await api.post(`/client-admin/officials/${official.id}`, formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } else {
          response = await api.post('/client-admin/officials', formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        }
      } else {
        // No image, send JSON with proper method
        if (official) {
          response = await api.put(`/client-admin/officials/${official.id}`, dataToSubmit)
        } else {
          response = await api.post('/client-admin/officials', dataToSubmit)
        }
      }

      const result = response.data

      if (result.success) {
        const message = official ? 'Official updated successfully' : 'Official created successfully'
        onSuccess(message)
      } else {
        showToast?.error(result.message || 'Failed to save official')
      }
    } catch (err) {
      console.error('Error saving official:', err)
      showToast?.error('Error saving official')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="compact-official-form" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>
            <i className="fas fa-user-tie"></i>
            {official ? 'Edit Official' : 'Add New Official'}
          </h2>
          <button className="btn-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-body">
            {/* Profile Image Section */}
            <div className="form-section">
              <h3><i className="fas fa-image"></i> Profile Image</h3>
              <div className="image-upload-section">
                <div className="image-preview">
                  {imagePreview ? (
                    <div className="preview-container">
                      <img src={imagePreview} alt="Official preview" />
                      <button
                        type="button"
                        className="btn-remove-image"
                        onClick={handleRemoveImage}
                        title="Remove image"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="no-image">
                      <i className="fas fa-user-circle"></i>
                      <p>No image uploaded</p>
                    </div>
                  )}
                </div>
                <div className="upload-controls">
                  <label htmlFor="image-upload" className="btn-upload">
                    <i className="fas fa-upload"></i>
                    Choose Image
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <p className="upload-hint">Max file size: 5MB. Supported formats: JPG, PNG, GIF</p>
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-user"></i> Personal Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={errors.full_name ? 'error' : ''}
                    placeholder="Enter full name"
                  />
                  {errors.full_name && <span className="error-text">{errors.full_name}</span>}
                </div>

                <div className="form-group">
                  <label>Father's Name</label>
                  <input
                    type="text"
                    name="father_name"
                    value={formData.father_name}
                    onChange={handleChange}
                    placeholder="Enter father's name"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Date of Birth <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={errors.date_of_birth ? 'error' : ''}
                  />
                  {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                  {currentAge && (
                    <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      Age: {currentAge} years {formData.category && `- Category: ${formData.category}`}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Blood Group</label>
                  <select
                    name="blood_group"
                    value={formData.blood_group}
                    onChange={handleChange}
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Height (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="Enter height"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="Enter weight"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Marital Status</label>
                  <select
                    name="marital_status"
                    value={formData.marital_status}
                    onChange={handleChange}
                  >
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>

                {formData.marital_status === 'married' && (
                  <div className="form-group">
                    <label>Marriage Date</label>
                    <input
                      type="date"
                      name="marriage_date"
                      value={formData.marriage_date}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-phone"></i> Contact Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Contact Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    className={errors.contact ? 'error' : ''}
                    placeholder="Enter 10-digit contact number"
                    maxLength="10"
                  />
                  {errors.contact && <span className="error-text">{errors.contact}</span>}
                </div>

                <div className="form-group">
                  <label>Emergency Contact</label>
                  <input
                    type="tel"
                    name="emergency_contact"
                    value={formData.emergency_contact}
                    onChange={handleChange}
                    placeholder="Enter emergency contact"
                    maxLength="10"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="Enter email address"
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter complete address"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    placeholder="Enter district"
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <select
                    name="state_name"
                    value={formData.state_name}
                    onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                    <option value="Chandigarh">Chandigarh</option>
                    <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                    <option value="Ladakh">Ladakh</option>
                    <option value="Lakshadweep">Lakshadweep</option>
                    <option value="Puducherry">Puducherry</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Pin Code</label>
                  <input
                    type="text"
                    name="pin_code"
                    value={formData.pin_code}
                    onChange={handleChange}
                    placeholder="Enter pin code"
                    maxLength="6"
                  />
                </div>
              </div>
            </div>

            {/* Official Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-briefcase"></i> Official Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Official Type</label>
                  <select
                    name="official_type"
                    value={formData.official_type}
                    onChange={handleChange}
                  >
                    <option value="state_official">State Official</option>
                    <option value="tournament_official">Tournament Official</option>
                    <option value="other_official">Other Official</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    placeholder="Enter position"
                  />
                </div>

                {currentAge && formData.category && (
                  <div className="form-group">
                    <label>Category (Auto-calculated from DOB)</label>
                    <input
                      type="text"
                      value={formData.category}
                      disabled
                      style={{ background: '#f3f4f6', cursor: 'not-allowed', color: '#022a5e', fontWeight: '500' }}
                    />
                    <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      Category is automatically set based on date of birth
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Identity Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-id-card"></i> Identity Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Aadhar Card Number</label>
                  <input
                    type="text"
                    name="aadhar_card"
                    value={formData.aadhar_card}
                    onChange={handleChange}
                    placeholder="Enter 12-digit Aadhar number"
                    maxLength="12"
                  />
                </div>

                <div className="form-group">
                  <label>Passport Number</label>
                  <input
                    type="text"
                    name="passport_number"
                    value={formData.passport_number}
                    onChange={handleChange}
                    placeholder="Enter passport number"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              <i className="fas fa-times"></i>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {official ? 'Update Official' : 'Save Official'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CompactOfficialForm